import { useEffect, useState } from "react";
import { toast } from "sonner";
import { openDocument } from "@/lib/documents";
import { rpcErrorMessage } from "@/lib/rpc-error";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, Plus, Printer } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { usePurchaseOrders, usePoLookups } from "@/features/purchase-orders/api";
import { PO_STATUSES } from "@/features/purchase-orders/types";
import { poEmptyMessage, poPageSummary } from "@/features/purchase-orders/listParams";
import { PoStatusBadge } from "@/features/purchase-orders/PoStatusBadge";
import { ActionGuard } from "@/components/auth/ActionGuard";
import { ReadOnlyPill } from "@/components/auth/ReadOnlyPill";
import { useModuleAccess } from "@/hooks/useIsReadOnly";
import { ListExportButtons } from "@/features/reports/ListExportButtons";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canExportPurchaseReports } from "@/lib/roles";
import { useBranch } from "@/context/BranchContext";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/purchase-orders/")({
  component: PurchaseOrdersIndex,
});

function PurchaseOrdersIndex() {
  const navigate = useNavigate();
  const { canWrite } = useModuleAccess("purchase_orders");
  const { data: me } = useCurrentUser();
  const canExport = canExportPurchaseReports(me?.roles);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");   // what the server is actually searching for (debounced)
  const [status, setStatus] = useState("all");
  const [vendorId, setVendorId] = useState("all");
  const [page, setPage] = useState(1);
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;
  const { data: lookups } = usePoLookups();
  useEffect(() => {
    const t = setTimeout(() => setAppliedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);
  const filters = {
    search: appliedSearch,
    status: status === "all" ? null : status,
    vendorId: vendorId === "all" ? null : vendorId,
    branchId: effectiveBranchId,
  };
  // any change of the search, filters or branch starts again from the first page
  useEffect(() => { setPage(1); }, [appliedSearch, status, vendorId, effectiveBranchId]);
  const { data, isLoading, isError, error, refetch } = usePurchaseOrders(filters, page);
  const rows = data?.rows ?? [];
  const total = data?.count ?? 0;
  const summary = poPageSummary(total, page);

  const buildExportRows = () =>
    rows.map((r) => ({
      "PO Number": r.po_number,
      "PO Date": r.po_date ?? "",
      Vendor: r.vendor?.name ?? "",
      Branch: r.branch?.name ?? "",
      Department: r.department?.name ?? "",
      Status: r.status,
      Subtotal: Number(r.subtotal ?? 0),
      Tax: Number(r.tax_amount ?? 0),
      "Grand Total": Number(r.total_amount ?? 0),
      "Expected Delivery": r.expected_delivery_date ?? "",
    }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Purchase Orders</h1> <ReadOnlyPill module="purchase_orders" />
          <p className="text-sm text-muted-foreground">Multi-line POs for procurement.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListExportButtons
            rows={buildExportRows}
            filename="PO_Register"
            sheetName="PO Register"
            disabled={isLoading || rows.length === 0}
            canExport={canExport}
          />
          <ActionGuard can={canWrite}>
            <Button onClick={() => navigate({ to: "/purchase-orders/new" })}><Plus className="mr-1 h-4 w-4" />New PO</Button>
          </ActionGuard>
        </div>
      </div>

      <Card><CardContent className="p-3 flex flex-wrap items-center gap-2">
        <Input className="w-64" placeholder="Search PO number, vendor or indent…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search purchase orders" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {PO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={vendorId} onValueChange={setVendorId}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Vendor" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">All vendors</SelectItem>
            {(lookups?.vendors ?? []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
          isError ? (
            <div className="p-6 text-center text-sm text-destructive">
              Could not load purchase orders: {(error as Error)?.message || "unknown error"}{" "}
              <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
            </div>
          ) :
          rows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">{poEmptyMessage(filters)}</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>PO Number</TableHead>
                <TableHead>Indent</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>PO Date</TableHead>
                <TableHead>Status</TableHead>
                {/* <TableHead>Vendor Ack</TableHead> */}
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right w-20">Print</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="cursor-pointer" onClick={() => navigate({ to: "/purchase-orders/$poId", params: { poId: r.id } })}>
                    <TableCell className="font-medium">{r.po_number}{(r as { procurement_method?: string }).procurement_method === "local_purchase" && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">Local</span>}</TableCell>
                    <TableCell>{r.indent_number ?? "—"}</TableCell>
                    <TableCell>{r.vendor?.name ?? "—"}</TableCell>
                    <TableCell>{r.branch?.name ?? "—"}</TableCell>
                    <TableCell>{r.po_date}</TableCell>
                    <TableCell><PoStatusBadge status={r.status} /></TableCell>
                    {/* <TableCell className="text-xs">
                      {r.acknowledged_at
                        ? <span className="inline-flex items-center rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 px-2 py-0.5">Acknowledged · {formatDate(r.acknowledged_at)}</span>
                        : <span className="text-muted-foreground">Pending</span>}
                    </TableCell> */}
                    <TableCell className="text-right tabular-nums">{Number(r.total_amount).toFixed(2)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Print PO"
                        disabled={!r.approved_for_issue}
                        title={r.approved_for_issue ? "Print PO" : "Available after final approval"}
                        onClick={() => openDocument(`/api/v1/procurement/purchase-orders/${r.id}/document/`).catch((e) => toast.error(rpcErrorMessage(e)))}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        {total > 0 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
            <span>Showing {summary.from}–{summary.to} of {total}</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <span>Page {page} of {summary.pages}</span>
              <Button size="sm" variant="outline" disabled={page >= summary.pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
}
